<template>
  <label :class="componentClasses">
    <div class="stk-toggle-label">
      <input
        class="stk-toggle-input"
        type="checkbox"
        :checked="isChecked"
        :disabled="disabled || loading"
        @change="handleToggle"
      >
      <span class="stk-toggle-slider">
        <Loader v-if="loading" />
      </span>
    </div>
    <slot />
  </label>
</template>

<script setup>
import { computed, defineProps, defineEmits, ref } from 'vue';
import Loader from '@stakeui/graphic/loader/Loader.vue';

const props = defineProps({
  /* Mode 1: v-model */
  value: { type: Boolean, default: undefined },
  modelValue: { type: Boolean, default: undefined },

  /* Mode 2: controlled without v-model */
  checked: { type: Boolean, default: undefined },

  /* Mode 3: uncontrolled (internal state) */
  defaultChecked: { type: Boolean, default: false },

  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'input',
  'update:modelValue',
  'update:checked',
  'change', // emit(next, event)
  'toggle', // alias event
]);

// Internal state for uncontrolled mode
const inner = ref(props.defaultChecked);

// Single source of truth
const isChecked = computed(() => {
  // Vue 3 v-model
  if (props.modelValue !== undefined) return props.modelValue;

  // Vue 2 v-model
  if (props.value !== undefined) return props.value;

  // Controlled
  if (props.checked !== undefined) return props.checked;

  return inner.value;
});

const handleToggle = event => {
  const next = !isChecked.value;

  // Vue 3 v-model
  emit('update:modelValue', next);

  // Vue 2 v-model
  emit('input', next);

  emit('update:checked', next);
  emit('change', next, event);
  emit('toggle', next, event);

  // Uncontrolled fallback
  inner.value = next;
};

const componentClasses = computed(() => [
  'stk-toggle',
  isChecked.value ? 'is-checked' : '',
  props.loading ? 'is-loading' : '',
  props.variant !== '' ? `variant-${props.variant}` : '',
]);
</script>

<style>
.stk-toggle {
  --bg: var(--stk-primary-85);
  --fg: var(--stk-primary);
  align-items: center;
  color: var(--stk-text-strong);
  cursor: pointer;
  display: inline-flex;
  font-weight: 500;
  gap: 0.5em;
  vertical-align: top;

  &.variant-disabled {
    --bg: var(--stk-text-80);
    --fg: var(--stk-text-40);
  }

  &.variant-success {
    --bg: var(--stk-success-80);
    --fg: var(--stk-success-40);
  }

  &.variant-warning {
    --bg: var(--stk-warning-80);
    --fg: var(--stk-warning-40);
  }

  &.variant-danger {
    --bg: var(--stk-danger-80);
    --fg: var(--stk-danger-40);
  }
}

.stk-toggle-label {
  background-color: var(--stk-border);
  border-radius: 9999px;
  cursor: pointer;
  display: inline-flex;
  padding: 0.25em;
  position: relative;
  width: 2.5em;
}

.stk-toggle-input {
  opacity: 0;
  height: 0;
  width: 0;
}

.stk-toggle-slider {
  background-color: var(--stk-text-weak);
  border-radius: 9999px;
  cursor: pointer;
  display: flex;
  height: 1em;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color, transform;
  width: 1em;
}

/* Loading state */
.stk-toggle.is-loading .stk-toggle-slider {
  background-color: transparent !important;
}

/* Checked state */
.stk-toggle.is-checked .stk-toggle-label {
  background-color: var(--bg);
}

.stk-toggle.is-checked .stk-toggle-slider {
  background-color: var(--fg);
  transform: translateX(1em);
}
</style>
